import { useAccount } from "@apis/accounts/useAccounts"
import type { IAccount } from "@apis/accounts/types"
import type { TTitleValueItem } from "@components/TitleValueList/types"
import { formatDate } from "@utils/formatters"
import { DoorOpen, SquarePen } from "lucide-react"
import { useHasPermissions } from "@hooks/useHasPermissions"
import { useDrawer } from "@contexts/DrawerContext"
import { UpdateAccountStatusForm } from "../UpdateAccountStatusForm"
import { ActivateAccountForm } from "../ActivateAccountForm"
import { cn } from "@components/ui/lib/utils"
import { useGetOrderDetailQuery } from "@apis/orders/useOrders"

type TAccountStatusData = {
  items: TTitleValueItem[]
}

type TStatusContentProps = {
  accountData: IAccount | undefined
}

const ActiveContent = ({ accountData }: TStatusContentProps) => {
  const { canDropAccount } = useHasPermissions()
  const { openDrawer } = useDrawer()

  const confirmedStartDt = formatDate(accountData?.confirmed_start_dt)
  const confirmedDropDt = formatDate(accountData?.confirmed_drop_dt)

  if (confirmedStartDt && confirmedDropDt) {
    return `${confirmedStartDt} - ${confirmedDropDt}`
  }

  return (
    <div className="tw:flex tw:items-center tw:gap-1">
      {confirmedStartDt}
      {canDropAccount && (
        <SquarePen
          size={16}
          className="tw:hover:cursor-pointer"
          onClick={() =>
            openDrawer(
              <UpdateAccountStatusForm
                account_id={String(accountData?.account_id)}
              />,
              "Update Last Date of Service",
            )
          }
        />
      )}
    </div>
  )
}

const RejectedContent = ({ accountData }: TStatusContentProps) => {
  const { canActivateAccount } = useHasPermissions()
  const { openDrawer } = useDrawer()

  return (
    <div className="tw:flex tw:items-center tw:gap-1">
      {formatDate(accountData?.status_change_dt)}
      {canActivateAccount && (
        <DoorOpen
          size={16}
          className="tw:hover:cursor-pointer"
          onClick={() =>
            openDrawer(
              <ActivateAccountForm
                account_id={String(accountData?.account_id)}
              />,
              "Update Start Date of Service",
            )
          }
        />
      )}
    </div>
  )
}

const AccountStatusContent = ({ accountData }: TStatusContentProps) => {
  const { canActivateAccount } = useHasPermissions()
  const { openDrawer } = useDrawer()

  const statusChangeDt = formatDate(accountData?.status_change_dt)
  const accountStatusGroupCancelled =
    accountData?.status_group === "Cancelled"

  return (
    <div className="tw:flex tw:items-center tw:gap-1">
      {statusChangeDt
        ? `${accountData?.status_description} as of ${statusChangeDt}`
        : accountData?.status_description}

      {accountStatusGroupCancelled && canActivateAccount && (
        <DoorOpen
          size={16}
          className="tw:hover:cursor-pointer"
          onClick={() =>
            openDrawer(
              <ActivateAccountForm
                account_id={String(accountData?.account_id)}
              />,
              "Update Start Date of Service",
            )
          }
        />
      )}
    </div>
  )
}

export const useAccountStatusSerializer = (account_id: string) => {
  const getAccount = useAccount(account_id)
  const getOrderDetailQuery = useGetOrderDetailQuery(getAccount.data?.order_id)

  const data: TAccountStatusData = {
    items: [
      {
        title: { content: "Account Status:" },
        value: {
          content: <AccountStatusContent accountData={getAccount.data} />,
          isPending: getAccount.isPending,
        },
      },
      {
        title: { content: "Signed Up:" },
        value: {
          content: formatDate(getAccount.data?.sale_dt),
          isPending: getAccount.isPending,
        },
      },
      {
        title: { content: "Active:" },
        value: {
          content: <ActiveContent accountData={getAccount.data} />,
          isPending: getAccount.isPending,
        },
        className: cn({
          "tw:hidden": !getAccount.data?.confirmed_start_dt,
        }),
      },
      {
        title: { content: "Rejected:" },
        value: {
          content: <RejectedContent accountData={getAccount.data} />,
          isPending: getAccount.isPending,
        },
        className: cn({
          "tw:hidden": getAccount.data?.status_group !== "Rejected",
        }),
      },
      {
        title: { content: "Confirmation Code:" },
        value: {
          content: getOrderDetailQuery.data?.confirmation_code,
          isPending: getOrderDetailQuery.isPending,
        },
      },
    ],
  }

  return {
    data,
    isError: getAccount.isError,
  }
}
